import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeService } from '../services/themeService';

interface ThemeContextType {
  isDark: boolean;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Apply dark mode CSS variables
const applyDarkModeVariables = (isDark: boolean) => {
  const root = document.documentElement;

  if (isDark) {
    // Dark mode colors
    /*
     * --theme-header-bg / --theme-header-fg are deliberately NOT written here.
     *
     * The header bar is dark in BOTH themes (it paints --theme-header-grad-*,
     * which nothing outside index.css sets), so its foreground is not a function
     * of the dark-mode flag. This branch used to write a light fg and the light
     * branch a near-black one — inline on documentElement, which beats index.css
     * — so in light mode the user's name was painted #111827 onto a near-black
     * bar and simply disappeared. index.css owns both tokens now.
     */
    // Same rail, taken down — see the light branch below.
    root.style.setProperty('--theme-sidemenu-bg', '#08080c');
    root.style.setProperty('--theme-sidemenu-grad-from', '#0d0d13');
    root.style.setProperty('--theme-sidemenu-grad-to', '#060609');
    /*
     * IDLE LABELS ARE NOT WHITE — see the token block in index.css.
     * This used to write #ffffff, inline on documentElement, which beat the
     * stylesheet: the rail's whole text hierarchy (muted idle / white current)
     * collapsed back to one voice the moment the theme was applied.
     */
    root.style.setProperty('--theme-sidemenu-fg', '#a6a8b8');
    root.style.setProperty('--theme-sidemenu-hover', 'rgba(255, 255, 255, 0.05)');
    root.style.setProperty('--theme-sidemenu-hover-fg', '#ffffff');
    /* A violet-tinted sheet, not a solid violet key — the spine at the rail's
       edge is what marks the row now. */
    root.style.setProperty('--theme-sidemenu-active', 'rgba(139, 92, 246, 0.19)');
    root.style.setProperty('--theme-body-bg', '#100d18');
    root.style.setProperty('--theme-body-fg', '#f3f4f6');
    root.style.setProperty('--theme-text-primary', '#f3f4f6');
    root.style.setProperty('--theme-text-secondary', '#d1d5db');
    root.style.setProperty('--theme-text-muted', '#9ca3af');
    root.style.setProperty('--theme-bg-secondary', '#1f2937');
    root.style.setProperty('--theme-bg-tertiary', '#374151');
    root.style.setProperty('--theme-border', '#374151');
    root.style.setProperty('--theme-border-light', '#4b5563');
    root.style.setProperty('--header-bg-color', '#1f2937');
    root.style.setProperty('--header-text-color', '#f3f4f6');
    root.style.setProperty('--group-header-color', '#374151');
    root.style.setProperty('--header-border-color', '#374151');
    root.style.setProperty('--header-hover-bg', '#374151');
    root.style.setProperty('--header-active-bg', '#4b5563');
  } else {
    // Light mode colors
    // Header tokens intentionally omitted — see the dark branch above.
    /*
     * The ink rail, matching :root in index.css and the default preset in
     * themeService.
     *
     * These five are written INLINE on documentElement, which beats index.css's
     * :root no matter what order they load in — so a rail defined only in the
     * stylesheet is a rail nobody sees. That is why the surface (grad-from/to)
     * lives in index.css and the foreground lived here: the two halves were set
     * in different places and had drifted apart. They now agree.
     */
    root.style.setProperty('--theme-sidemenu-bg', '#0b0b11');
    root.style.setProperty('--theme-sidemenu-grad-from', '#12121a');
    root.style.setProperty('--theme-sidemenu-grad-to', '#0a0a0f');
    /* Muted idle label — see the dark branch above. */
    root.style.setProperty('--theme-sidemenu-fg', '#b0b2c2');
    root.style.setProperty('--theme-sidemenu-hover', 'rgba(255, 255, 255, 0.055)');
    root.style.setProperty('--theme-sidemenu-hover-fg', '#ffffff');
    root.style.setProperty('--theme-sidemenu-active', 'rgba(139, 92, 246, 0.17)');
    root.style.setProperty('--theme-body-bg', '#f6f4fb');
    root.style.setProperty('--theme-body-fg', '#111827');
    root.style.setProperty('--theme-text-primary', '#111827');
    root.style.setProperty('--theme-text-secondary', '#6b7280');
    root.style.setProperty('--theme-text-muted', '#9ca3af');
    root.style.setProperty('--theme-bg-secondary', '#ffffff');
    root.style.setProperty('--theme-bg-tertiary', '#f3f4f6');
    root.style.setProperty('--theme-border', '#e5e7eb');
    root.style.setProperty('--theme-border-light', '#f3f4f6');
    root.style.setProperty('--header-bg-color', '#f9fafb');
    root.style.setProperty('--header-text-color', '#374151');
    root.style.setProperty('--group-header-color', '#dbeafe');
    root.style.setProperty('--header-border-color', '#e5e7eb');
    root.style.setProperty('--header-hover-bg', '#f3f4f6');
    root.style.setProperty('--header-active-bg', '#e5e7eb');
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDark, setIsDark] = useState(() => {
    // Check for saved preference or system preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    // Check system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  useEffect(() => {
    // Apply dark class and CSS variables on initial load
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    applyDarkModeVariables(isDark);

    // Apply dark mode overrides to ThemeService (for inline styles on sidebar/header)
    ThemeService.applyDarkModeOverrides();
  }, [isDark]);

  const toggleTheme = () => {
    const newIsDark = !isDark;
    setIsDark(newIsDark);

    if (newIsDark) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }

    // Apply CSS variables for dark/light mode
    applyDarkModeVariables(newIsDark);

    // Apply dark mode overrides to ThemeService (for inline styles on sidebar/header)
    ThemeService.applyDarkModeOverrides();
  };

  return (
    <ThemeContext.Provider value={{ isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};